<template>
    <div>
        <div class="row m-t-6">
            <aside class="column small-12 medium-3">
                <div class="card m-b-4 p-4 br">
                    <div class="form-group form-group-compact">
                        <label class="sc-reader">{{ trans('Search') }}</label>
                        <div class="form-group-addon">
                            <input class="input__field input-small" type="text" v-bind:placeholder="trans('Search Subscriptions')" v-model="query" v-on:keydown.enter="filterSubscriptions">
                            <span class="form-append i-container btn btn-text i-filled-dark width-auto-min p-r-0">
                                <!-- /icons/search.svg -->
                                <svg class="icon m-l-1 i-size-small" width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M16.7 15.3c.9-1.2 1.4-2.6 1.4-4.2 0-3.9-3.1-7.1-7-7.1S4 7.2 4 11.1c0 3.9 3.2 7.1 7.1 7.1 1.6 0 3.1-.5 4.2-1.4l3 3c.2.2.5.3.7.3.2 0 .5-.1.7-.3.4-.4.4-1 0-1.4l-3-3.1zm-5.6.8c-2.8 0-5.1-2.2-5.1-5S8.3 6 11.1 6s5.1 2.3 5.1 5.1-2.3 5-5.1 5z" class="icon"/></svg>
                            </span>
                        </div>
                    </div>
                    <div class="form-group form-group-compact">
                        <label class="sc-reader">{{ trans('Membership') }}</label>
                        <select class="input-small input__field" v-model="selectedMembership">
                            <option value="">{{ trans('All Memberships') }}</option>
                            <option v-for="membership in memberships" v-bind:value="membership.encoded_id" v-bind:key="membership.encoded_id">{{ membership.name }}</option>
                        </select>
                    </div>
                    <div class="form-group form-group-compact">
                        <label class="sc-reader">{{ trans('Status') }}</label>
                        <select class="input-small input__field" v-model="selectedStatus">
                            <option value="">{{ trans('All Statuses') }}</option>
                            <option value="active">{{ trans('Active') }}</option>
                            <option value="overdue">{{ trans('Past Due') }}</option>
                            <option value="cancelled">{{ trans('Cancelled') }}</option>
                            <option value="refunded">{{ trans('Refunded') }}</option>
                            <option value="trialing">{{ trans('Trialing') }}</option>
                            <option value="completed">{{ trans('Completed') }}</option>
                        </select>
                    </div>
                    <button v-on:click="filterSubscriptions" v-bind:class="{'btn-loading': searching}" type="button" class="btn btn-primary btn--block m-b-2">
                        {{ trans('Search') }}
                        <div v-if="searching" class="loader">
                            <div class="chartdonut chartdonut_animation--rotate-tiny chartdonut--tiny">
                                <svg class="i-stroke-bright" width="24" height="24"><g><circle class="icon" cx="50%" cy="50%" r="10" stroke-width="2" fill="none" stroke-dashoffset="5"></circle></g></svg>
                            </div>
                        </div>
                    </button>
                    <div class="text-center">
                        <button v-on:click="resetFilter" type="button" class="btn btn-text btn-text--muted width-auto-min">{{ trans('Reset Filter') }}</button>
                    </div>
                </div>
            </aside>

            <div class="sales-content column small-12 medium-8 large-9">
                <div v-bind:class="{'table-group': paginator.lastPage > 1}">
                    <div class="table-header row row-collapse row-middle br-bottom m-b-5 p-b-1">
                        <div class="text-left">
                            <span v-if="loading" class="c-medium m-r-r f-s-3">{{ trans('Loading subscriptions...') }}</span>
                            <span v-else class="c-medium m-r-3 f-s-3">{{ trans(':num subscriptions', {'num': paginator.total}) }}</span>
                        </div>
                        <div class="column text-left">
                            <button v-on:click="switchEnvironment" type="button" class="f-s-3 c-accent h-c-accent cu-p m-r-3">
                                <span class="i-container i-filled-accent">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" class="injected-svg icon inject-svg i-size-small"><g transform="translate(4 6)" class="icon"><path d="M8 12c4.707 0 7.744-5.284 7.871-5.508a1 1 0 0 0 .001-.98C15.746 5.287 12.731 0 8 0 3.245 0 .251 5.289.126 5.514a.998.998 0 0 0 .002.975C.254 6.713 3.269 12 8 12zM8 2c2.839 0 5.036 2.835 5.818 4-.784 1.166-2.981 4-5.818 4-2.841 0-5.038-2.838-5.819-4.001C2.958 4.835 5.146 2 8 2z"></path><circle cx="8" cy="6" r="2"></circle></g></svg>
                                </span>
                                <span v-if="selectedEnvironment === 'live'" class="inline">{{ trans('View Test Data') }}</span>
                                <span v-else class="inline">{{ trans('View Live Data') }}</span>
                            </button>
                        </div>
                    </div>
                <div class="table-overflow-set">
                        <div v-if="loading" style="margin: 0 auto;" class="chartdonut chartdonut_animation--rotate-large chartdonut--large">
                            <svg id="svgElement" width="53" height="53"><g><circle cx="50%" cy="50%" r="23" stroke-width="3" stroke="#02DCAA" fill="none" stroke-dashoffset="5"></circle></g></svg>
                        </div>
                        <div v-if=" ! loading && items.length === 0" class="text-center m-t-6">
                            <p v-if="selectedEnvironment === 'live'" class="c-medium f-s-3">{{ trans('No subscriptions found.  If you\'re looking for "test" subscriptions - click View test data above') }}</p>
                            <p v-else class="c-medium f-s-3">{{ trans('No subscriptions found.') }}</p>
                        </div>
                        <table v-if=" ! loading && items.length > 0" class="table" style="margin-bottom: 54px;">
                            <thead>
                                <tr>
                                    <th class="br-bottom br-c--light c-medium-tint f-w-regular f-s-3">{{ trans('Member') }}</th>
                                    <th class="br-bottom br-c--light c-medium-tint f-w-regular f-s-3">{{ trans('Amount') }}</th>
                                    <th class="br-bottom br-c--light c-medium-tint f-w-regular f-s-3">{{ trans('Plan') }}</th>
                                    <th class="br-bottom br-c--light c-medium-tint f-w-regular f-s-3" width="120px">{{ trans('Renews') }}</th>
                                    <th class="br-bottom br-c--light c-medium-tint f-w-regular f-s-3" width="100px">{{ trans('Status') }}</th>
                                    <th class="br-bottom br-c--light c-medium-tint f-w-regular f-s-3" width="36px">&nbsp;</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="item in items" v-bind:key="item.id">
                                    <td v-bind:data-heading="trans('Member')">{{ item.member_name }}</td>
                                    <td v-bind:data-heading="trans('Amount')">{{ item.pricing_plan_amount }}</td>
                                    <td v-bind:data-heading="trans('Plan')">
                                        <div>{{ item.pricing_plan_name }}</div>
                                        <div class="c-muted f-s-2">{{ item.membership_name }}</div>
                                    </td>
                                    <td v-bind:data-heading="trans('Renews')">{{ formatDate(item.renewal_date) }}</td>
                                    <td v-bind:data-heading="trans('Status')">
                                        <subscription-status
                                            v-bind:status="item.status"
                                        ></subscription-status>
                                    </td>
                                    <td class="text-right">
                                        <subscription-dropdown-menu
                                            v-if="canCancelSubscriptions && item.status !== 'cancelled'"
                                            v-bind:member-id="item.member_encoded_id"
                                            v-on:cancel="showSubscriptionCancelModal(item.encoded_id)"
                                        ></subscription-dropdown-menu>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
        <paginator
            v-if=" ! loading"
            v-bind:to="paginator.to"
            v-bind:from="paginator.from"
            v-bind:total="paginator.total"
            v-bind:last-page="paginator.lastPage"
            v-bind:current-page="paginator.currentPage"
            v-on:paginator:switch-page="changePage"
        ></paginator>
        <subscription-cancel-modal
            ref="subscriptionCancelModal"
            v-on:subscription:cancelled="changeSubscriptionStatus"
        ></subscription-cancel-modal>
    </div>
</template>
<script>
import Paginator from '../Paginator.vue';
import SubscriptionStatus from './SubscriptionStatus.vue';
import SubscriptionDropdownMenu from './SubscriptionDropdownMenu.vue';
import SubscriptionCancelModal from './SubscriptionCancelModal.vue';

export default {
    components: {
        Paginator,
        SubscriptionStatus,
        SubscriptionDropdownMenu,
        SubscriptionCancelModal,
    },
    props: {
        subscriptions: {
            type: [Array, Object]
        },
        memberships: {
            type: Array
        },
        canCancelSubscriptions: {
            type: Boolean,
            default: false
        },
        total: {
            type: Number,
            required: true
        },
        perPage: {
            type: Number,
            default: 20
        },
        lastPage: {
            type: Number,
            default: 1
        },
        environment: {
            type: String,
            default: 'live',
        },
    },
    data() {
        return {
            items: this.subscriptions,
            selectedMembership: "",
            selectedStatus: "",
            paginator: {
                total: this.total,
                to: this.perPage,
                from: 1,
                lastPage: this.lastPage,
                currentPage: 1,
            },
            loading: false,
            searching: false,
            query: null,
            selectedEnvironment: this.environment,
        }
    },
    methods: {
        switchEnvironment() {
            this.selectedEnvironment = this.selectedEnvironment === 'live' ? 'test' : 'live';

            this.loading = true;

            this.loadSubscriptions(1);
        },
        changePage(page) {
            this.loading = true;

            this.loadSubscriptions(page);
        },
        filterSubscriptions() {
            this.loading = true;
            this.searching = true;

            this.loadSubscriptions(1);
        },
        resetFilter() {
            this.loading = true;
            this.query = "";
            this.selectedStatus = "";
            this.selectedMembership = "";

            this.loadSubscriptions(1);
        },
        loadSubscriptions(page) {
            return window.axios.get(Kourses.apiBaseUrl + '/sales/subscriptions', {
                params: {
                    'page': page,
                    'query': this.query,
                    'status': this.selectedStatus,
                    'membership': this.selectedMembership,
                    'environment': this.selectedEnvironment,
                }
            }).then(this.showSubscriptions);
        },
        showSubscriptions(response) {
            this.paginator = {
                to: response.data.meta.to,
                from: response.data.meta.from,
                total: response.data.meta.total,
                lastPage: response.data.meta.last_page,
                currentPage: response.data.meta.current_page
            };
            this.items = response.data.data;
            this.loading = false;
            this.searching = false;
        },
        formatDate(rawDate) {
            if ( ! rawDate) {
                return '';
            }

            return new Date(rawDate).toLocaleDateString(window.Kourses.locale, {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
            });
        },
        showSubscriptionCancelModal(orderId) {
            this.$refs.subscriptionCancelModal.show = true;
            this.$refs.subscriptionCancelModal.orderId = orderId;
        },
        changeSubscriptionStatus(orderId) {
            let cancelledSubscription = this.items.find(item => item.encoded_id === orderId);

            if (cancelledSubscription) {
                cancelledSubscription.status = {
                    key: 'cancelled',
                    label: this.trans('Cancelled'),
                };
            }
        },
    },
}
</script>